import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Select, SelectProps} from 'antd';
import {UserDTO} from 'hemwb-api';
import {useDispatch} from 'react-redux';
import {userFind} from '../../../store/user';
import {fullName} from '../../../store/user/utils';
import styles from './UserSelect.module.scss';

export type UserSelectOption = {
  label: string;
  value: string;
  disabled?: boolean;
  user: UserDTO;
};

export interface UserSelectProps extends Omit<SelectProps, 'options' | 'loading' | 'onSearch'> {
  /**
   * Callback when a user is selected
   */
  onUserSelect?: (user: UserDTO) => void;

  /**
   * Function to determine if an option should be disabled
   */
  getOptionDisabled?: (user: UserDTO) => boolean;

  /**
   * Custom filter function for options (default: filters by label)
   */
  filterOption?: SelectProps['filterOption'];

  /**
   * Additional user find options
   */
  userFindOptions?: {
    limit?: number;
    includeNpa?: boolean;
    includeUnenrolled?: boolean;
  };

  /**
   * Debounce delay for user search in milliseconds (default: 300)
   */
  searchDebounce?: number;

  /**
   * Currently selected users to display (for displaying selected options before search)
   */
  selectedUsers?: UserDTO | UserDTO[];

  /**
   * If true, hides selected value/tags in the input and keeps placeholder visible.
   * Selection is still preserved in form state.
   */
  hideSelectedValue?: boolean;
}

/**
 * UserSelect - A reusable Ant Design Select component for user selection with built-in search
 *
 * Features:
 * - Debounced server-side user search
 * - Client-side filtering of loaded options
 * - Support for disabled options via getOptionDisabled
 * - Loading states
 * - Customizable via all standard Ant Design Select props
 *
 * @example
 * // Single user selection with disabled logic
 * <UserSelect
 *   placeholder="Find user"
 *   onChange={(value, option) => handleChange(value, option)}
 *   onUserSelect={(user) => setSelectedUser(user)}
 *   getOptionDisabled={(user) => existingUserIds.includes(user.id)}
 * />
 *
 * @example
 * // Multiple user selection
 * <UserSelect
 *   mode="multiple"
 *   placeholder="Find users"
 *   value={selectedUserIds}
 *   onChange={(values) => setSelectedUserIds(values)}
 *   getOptionDisabled={(user) => disabledUserIds.includes(user.id)}
 * />
 */
const UserSelect: React.FC<UserSelectProps> = ({
  onUserSelect,
  getOptionDisabled,
  filterOption,
  userFindOptions = {limit: 10, includeNpa: true},
  searchDebounce = 300,
  selectedUsers,
  hideSelectedValue = false,
  onChange,
  ...selectProps
}) => {
  const dispatch = useDispatch();
  const [isFocused, setIsFocused] = useState(false);

  // Initialize options with selected users immediately
  const getInitialOptions = useCallback(() => {
    if (!selectedUsers) return [];
    const users = Array.isArray(selectedUsers) ? selectedUsers : [selectedUsers];
    return users
      .filter((user) => user) // Filter out null/undefined
      .map((user) => ({
        label: `${fullName(user)} (${user.email})`,
        value: user.id,
        disabled: getOptionDisabled?.(user) ?? false,
        user,
      }));
  }, [selectedUsers, getOptionDisabled]);

  const [options, setOptions] = useState<UserSelectOption[]>(getInitialOptions());
  const [loading, setLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const lastSearchRef = useRef<string>('');

  // Initialize options with selected users
  useEffect(() => {
    if (selectedUsers) {
      const users = Array.isArray(selectedUsers) ? selectedUsers : [selectedUsers];
      const initialOptions: UserSelectOption[] = users
        .filter((user) => user) // Filter out null/undefined
        .map((user) => ({
          label: `${fullName(user)} (${user.email})`,
          value: user.id,
          disabled: getOptionDisabled?.(user) ?? false,
          user,
        }));
      setOptions(initialOptions);
    }
  }, [selectedUsers, getOptionDisabled]);

  const handleSearch = useCallback(
    (searchTerm: string) => {
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // If empty search, reset to showing only selected users
      if (!searchTerm.trim()) {
        if (selectedUsers) {
          const users = Array.isArray(selectedUsers) ? selectedUsers : [selectedUsers];
          const initialOptions: UserSelectOption[] = users
            .filter((user) => user) // Filter out null/undefined
            .map((user) => ({
              label: `${fullName(user)} (${user.email})`,
              value: user.id,
              disabled: getOptionDisabled?.(user) ?? false,
              user,
            }));
          setOptions(initialOptions);
        } else {
          setOptions([]);
        }
        return;
      }

      // Store the current search term
      const currentSearch = searchTerm;
      lastSearchRef.current = currentSearch;

      // Debounce the search
      searchTimeoutRef.current = setTimeout(async () => {
        // Ignore if a newer search has been initiated
        if (lastSearchRef.current !== currentSearch) {
          return;
        }

        setLoading(true);
        try {
          const users = await userFind(dispatch, searchTerm, userFindOptions);

          // Only update if this is still the latest search
          if (lastSearchRef.current === currentSearch) {
            const userOptions: UserSelectOption[] = users.map((user) => ({
              label: `${fullName(user)} (${user.email})`,
              value: user.id,
              disabled: getOptionDisabled?.(user) ?? false,
              user,
            }));

            // Merge with selected users to ensure they're always available
            const selectedUsersList = selectedUsers
              ? Array.isArray(selectedUsers)
                ? selectedUsers
                : [selectedUsers]
              : [];
            const selectedOptions: UserSelectOption[] = selectedUsersList
              .filter((user) => user && !users.some((u) => u.id === user.id)) // Don't duplicate
              .map((user) => ({
                label: `${fullName(user)} (${user.email})`,
                value: user.id,
                disabled: getOptionDisabled?.(user) ?? false,
                user,
              }));

            setOptions([...selectedOptions, ...userOptions]);
          }
        } catch (e) {
        } finally {
          // Only update loading state if this is still the latest search
          if (lastSearchRef.current === currentSearch) {
            setLoading(false);
          }
        }
      }, searchDebounce);
    },
    [dispatch, userFindOptions, getOptionDisabled, searchDebounce, selectedUsers],
  );

  const handleChange: SelectProps['onChange'] = useCallback(
    (value: any, option: any) => {
      // Call onUserSelect with the actual user object if provided
      if (onUserSelect && option) {
        if (Array.isArray(option)) {
          // Multiple selection - call for ALL selected users
          option.forEach((opt: any) => {
            const selectedOption = opt as UserSelectOption;
            if (selectedOption?.user) {
              onUserSelect(selectedOption.user);
            }
          });
        } else {
          // Single selection
          const selectedOption = option as UserSelectOption;
          if (selectedOption?.user) {
            onUserSelect(selectedOption.user);
          }
        }
      }

      // Call the original onChange if provided
      onChange?.(value, option);
    },
    [onChange, onUserSelect],
  );

  const isMultipleMode = selectProps.mode === 'multiple';
  const mergedClassName = [
    selectProps.className,
    hideSelectedValue && !isMultipleMode && !isFocused ? styles.hideSingleValue : '',
    hideSelectedValue && isMultipleMode ? styles.hideMultipleValue : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleFocus: SelectProps['onFocus'] = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      setIsFocused(true);
      selectProps.onFocus?.(event);
    },
    [selectProps],
  );

  const handleBlur: SelectProps['onBlur'] = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      setIsFocused(false);
      selectProps.onBlur?.(event);
    },
    [selectProps],
  );

  return (
    <Select
      showSearch
      {...selectProps}
      loading={loading}
      notFoundContent={loading ? 'Loading...' : 'No users found'}
      onSearch={handleSearch}
      filterOption={filterOption ?? false}
      options={options}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={mergedClassName || undefined}
      maxTagCount={hideSelectedValue && isMultipleMode ? 0 : selectProps.maxTagCount}
      maxTagPlaceholder={hideSelectedValue && isMultipleMode ? () => null : selectProps.maxTagPlaceholder}
    />
  );
};

export default UserSelect;
